import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { keyBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { Redirect } from 'react-router-dom';

import AddRate from '../components/AddRate';
import StickyPageHeader from '../components/common/StickyPageHeader';
import RatesFilter from '../components/filters/RatesFilter';
import RateTable from '../components/tables/RateTable';
import { AuthContext } from '../contexts/authContext';
import { useFileExport } from '../hooks';
import {
  useCarrierClasses,
  useCarrierOption,
  useCarrierTerminals,
  useDefaultModes,
  useModes,
  useSelectedCarrier,
  useShipTypes,
} from '../hooks/apiHooks';
import { useRates } from '../hooks/apiHooks/rate';
import Layout from '../layout';
import { isLocationMatch } from '../models/Rate';
import { UserRole } from '../models/User';

const CarrierRatesPage = ({ disableLayout, routes }) => {
  const queryClient = useQueryClient();
  const { authUser } = useContext(AuthContext);
  const { carrierId, selectedCarrier, status } = useSelectedCarrier();
  const { carrierClasses } = useCarrierClasses(carrierId);
  const { carrierOption } = useCarrierOption(carrierId);
  const { carrierTerminals } = useCarrierTerminals(carrierId);
  const { modes: carrierModes } = useModes(carrierId);
  const { allShipTypes } = useShipTypes();
  const { defaultModes } = useDefaultModes();
  const [filter, setFilter] = useState(null);
  const { isLoading, rates } = useRates(carrierId, filter);
  const { exportToCsv, exportToExcel, exportToPdf, isExporting } =
    useFileExport();
  const [highlightRateId, setHighlightRateId] = useState(null);

  const isAdmin = useMemo(
    () => [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(authUser.role),
    [authUser.role]
  );

  const filteredRates = useMemo(() => {
    if (!filter) return rates;
    // Check if it's a unified search
    const isUnifiedSearch = filter.searchType !== 'separate';
    const filtered = rates.filter(
      (rate) =>
        (!filter.carrierId || rate.carrierId === filter.carrierId) &&
        (!filter.modeNames || filter.modeNames.length === 0 || filter.modeNames.includes(rate.modeName)) &&
        (!filter.origin ||
          isLocationMatch(rate, filter.origin, true, isUnifiedSearch)) &&
        (!filter.destination ||
          isLocationMatch(rate, filter.destination, false, false))
    );

    // sort exact matches first
    if (filter.origin || filter.destination) {
      const originText = filter.origin?.trim().replace(/^["']|["']$/g, '').toLowerCase() || '';
      const destText = filter.destination?.trim().replace(/^["']|["']$/g, '').toLowerCase() || '';

      return filtered.sort((a, b) => {
        const aExact = (originText && a.origin.toLowerCase().includes(originText)) ||
          (destText && a.destination.toLowerCase().includes(destText));
        const bExact = (originText && b.origin.toLowerCase().includes(originText)) ||
          (destText && b.destination.toLowerCase().includes(destText));
        return aExact && !bExact ? -1 : !aExact && bExact ? 1 : 0;
      });
    }

    return filtered;
  }, [filter, rates]);

  const keyedShipTypes = useMemo(
    () => keyBy(allShipTypes, (shipType) => shipType.id),
    [allShipTypes]
  );
  const keyedCarrierModes = useMemo(
    () => keyBy(carrierModes ?? selectedCarrier?.modes, (mode) => mode.id),
    [carrierModes, selectedCarrier?.modes]
  );

  const selectedCarrierWithClasses = useMemo(
    () =>
      selectedCarrier
        ? {
          ...selectedCarrier,
          classes: carrierClasses ?? selectedCarrier.classes,
          modes: carrierModes ?? selectedCarrier.modes,
          option: carrierOption ?? selectedCarrier.option,
          terminals: carrierTerminals ?? selectedCarrier.terminals,
        }
        : selectedCarrier,
    [
      selectedCarrier,
      carrierClasses,
      carrierModes,
      carrierOption,
      carrierTerminals,
    ]
  );

  const exportData = useMemo(() => {
    return rates
      .map((rate) => {
        const classRates = {};
        if (selectedCarrier && carrierClasses?.length) {
          // csv export requires blank entry for every possible rate class, so we need to add a matching number or a blank
          carrierClasses.forEach((k) => {
            const classRate = rate.classRate[k.id];
            // get the value from ClassRate object (which has { value: number, updatedAt?: Date })
            const rateValue = classRate?.value ?? '';
            classRates[`class-${k.id}`] = rateValue;
          });
        }

        return {
          destType: keyedShipTypes[rate.destinationShipTypeId]?.name,
          destination: rate.destination,
          mode: keyedCarrierModes[rate.modeId]?.name,
          orgType: keyedShipTypes[rate.shipTypeId]?.name,
          origin: rate.origin,
          ...classRates,
          updatedAt: rate.updatedAt
            ? new Date(rate.updatedAt).toLocaleDateString('en-US')
            : '',
        };
      })
      .sort((a, b) =>
        `${a.origin} ${a.destination}` < `${b.origin} ${b.destination}` ? -1 : 1
      );
  }, [keyedCarrierModes, keyedShipTypes, rates, selectedCarrier, carrierClasses]);

  const exportColumns = useMemo(() => {
    if (selectedCarrier && carrierClasses?.length) {
      return [
        {
          selector: 'origin',
          title: 'Origin',
        },
        {
          selector: 'orgType',
          title: 'Org Type',
        },
        {
          selector: 'destination',
          title: 'Destination',
        },
        {
          selector: 'destType',
          title: 'Dest Type',
        },
        {
          selector: 'mode',
          title: 'Mode',
        },
        ...carrierClasses.map((k) => {
          // escaping commas and quotes
          let headerTitle = k.name || '';
          if (headerTitle.includes(',') || headerTitle.includes('"')) {
            headerTitle = `"${headerTitle.replace(/"/g, '""')}"`;
          }
          return {
            selector: `class-${k.id}`,
            title: headerTitle,
          };
        }),
      ];
    }
  }, [selectedCarrier, carrierClasses]);

  const onExport = useCallback(
    (type) => {
      const config = {
        columns: exportColumns,
        data: exportData,
        filename: selectedCarrier?.name || 'Carrier Rates',
        title: selectedCarrier?.name || '',
      };
      if (type === 'CSV') {
        exportToCsv(config);
      }

      if (type === 'XLSX' || type === 'XLS') {
        exportToExcel({
          ...config,
          type,
        });
      }
      if (type === 'PDF') {
        exportToPdf(config);
      }
    },
    [
      exportColumns,
      exportData,
      exportToCsv,
      exportToExcel,
      exportToPdf,
      selectedCarrier?.name,
    ]
  );

  useEffect(() => {
    queryClient.invalidateQueries(['rates', carrierId]);
  }, [carrierId, queryClient]);

  useEffect(() => {
    setFilter(null);
  }, [carrierId]);

  useEffect(() => {
    if (highlightRateId) {
      const timer = setTimeout(() => {
        setHighlightRateId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightRateId]);

  const handleGoToRate = useCallback((rateId) => {
    setHighlightRateId(rateId);
  }, []);

  const handleClearFiltersAndGoToRate = useCallback((rateId) => {
    setFilter(null);
    setHighlightRateId(rateId);
  }, []);

  if (status === 'error') {
    return <Redirect to="/" />;
  }

  const headerActions = authUser?.role !== UserRole.CUSTOMER && (
    <Dropdown
      overlay={
        <Menu onClick={(e) => onExport(e.key)}>
          <Menu.Item key="CSV">CSV</Menu.Item>
          <Menu.Item key="XLS">XLS</Menu.Item>
          <Menu.Item key="XLSX">XLSX</Menu.Item>
          <Menu.Item key="PDF">PDF</Menu.Item>
        </Menu>
      }
      placement="bottomRight"
      trigger={['click']}
    >
      <Button ghost loading={isExporting} type="primary">
        Export <DownOutlined />
      </Button>
    </Dropdown>
  );

  const content = (
    <>
      <StickyPageHeader
        actions={headerActions}
        subtitle={selectedCarrier?.name}
        title="Rates"
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {authUser?.role !== UserRole.CUSTOMER && (
          <AddRate
            rates={rates}
            selectedCarrier={selectedCarrierWithClasses}
            shipTypes={allShipTypes}
            onGoToRate={handleGoToRate}
          />
        )}
        <RatesFilter
          filter={filter}
          modes={defaultModes}
          onFilterChange={setFilter}
        />
        <RateTable
          highlightRateId={highlightRateId}
          isEditable={authUser?.role !== UserRole.CUSTOMER}
          loading={!selectedCarrier || (isLoading && !rates.length)}
          rates={selectedCarrier ? filteredRates : []}
          selectedCarrier={selectedCarrierWithClasses}
          shipTypes={allShipTypes}
          onClearFiltersAndGoToRate={handleClearFiltersAndGoToRate}
        />
        <p style={{ textAlign: 'right' }}>{filteredRates.length} rates</p>
      </Space>
    </>
  );

  if (disableLayout) {
    return content;
  }

  return (
    <Layout
      disableLoading
      skipFullPageLoader
      carrierLoading={!selectedCarrier}
      menuIndex={isAdmin ? 1 : 0}
      routes={routes}
      showCarriersNavigation={isAdmin}
    >
      {content}
    </Layout>
  );
};

export default CarrierRatesPage;
